import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  apiRequest,
  ApiRequestError,
  refreshAccessToken,
  setAccessToken,
  setSessionLostHandler,
} from '../lib/api-client';
import { AuthContext, type AuthContextValue, type CurrentUser } from './auth-store.js';

/**
 * Authentication state for the SPA.
 *
 * The access token lives in memory only: the refresh token is an httpOnly cookie the page cannot
 * read (docs/adr/0008-identity-and-sessions.md), so a reload recovers the session by refreshing
 * rather than by reading a token out of storage.
 */


interface LoginResponse {
  access_token: string;
  expires_in: number;
  user: CurrentUser;
}

/** Routes that trade a one-shot token for a session, and must not be raced by a restore. */
const SELF_ESTABLISHING = new Set(['/entrar/link', '/convite']);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refreshUser = useCallback(async () => {
    try {
      const profile = await apiRequest<CurrentUser>('/v1/auth/me');
      setUser(profile);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  /*
   * Signed out only when the session is really gone.
   *
   * The client renews an expired access token by itself; it calls this when the **refresh** fails,
   * which means the cookie expired or the session was revoked. Before this existed, an expired
   * access token simply made every screen show an error panel for ever.
   */
  useEffect(() => {
    setSessionLostHandler(() => {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
    });

    return () => setSessionLostHandler(null);
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;

    // The profile carries a signed picture link and the preferences; both go stale quietly. Re-read
    // it when the tab comes back after a while, and every half hour regardless.
    let last = Date.now();
    const refresh = (): void => {
      last = Date.now();
      void refreshUser();
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && Date.now() - last > 10 * 60_000) refresh();
    };
    const timer = window.setInterval(refresh, 30 * 60_000);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [status, refreshUser]);

  useEffect(() => {
    /*
     * Two pages establish a session of their own: the magic-link callback and the invitation.
     *
     * Restoring the previous session here would race them — both write the same refresh cookie, and
     * whichever finishes last wins. In practice that meant opening a sign-in link while an old
     * session existed sometimes left you signed in as whoever you were before, which is worse than
     * confusing on a shared machine.
     *
     * Those pages set the session themselves, so this settles as anonymous and gets out of the way.
     */
    if (SELF_ESTABLISHING.has(window.location.pathname)) {
      setStatus('anonymous');
      return;
    }

    // On load the access token is gone but the refresh cookie may still be valid.
    void (async () => {
      try {
        await refreshAccessToken();
        await refreshUser();
      } catch {
        setStatus('anonymous');
      }
    })();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiRequest<LoginResponse>('/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    });

    setAccessToken(result.access_token);
    setUser(result.user);
    setStatus('authenticated');
  }, []);

  const loginWithMagicLink = useCallback(async (token: string) => {
    const result = await apiRequest<LoginResponse>('/v1/auth/magic-link/consume', {
      method: 'POST',
      body: { token },
    });

    setAccessToken(result.access_token);
    setUser(result.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/v1/auth/logout', { method: 'POST' });
    } catch (error) {
      // A failed logout still ends the local session; the server-side session expires on its own.
      if (!(error instanceof ApiRequestError)) throw error;
    } finally {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo(
    () => ({ user, status, login, loginWithMagicLink, logout, refreshUser }),
    [user, status, login, loginWithMagicLink, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

